import {
  ProjectFolder,
  ProjectFile,
  FileQuery,
  FolderTreeNode,
  CreateFolderData,
  UpdateFolderData,
  CreateFileData,
  UpdateFileData,
  FileReference,
  CreateShareLinkData,
  ShareLink,
} from '../types';
import { apiService as api } from './httpClient';

// Response interfaces
interface FoldersListResponse {
  folders: ProjectFolder[];
  total?: number;
}

interface FolderTreeResponse {
  tree: FolderTreeNode[];
}

interface FilesListResponse {
  files: ProjectFile[];
  pagination?: {
    total: number;
    page: number;
    totalPages: number;
  };
  total?: number;
  page?: number;
  totalPages?: number;
}

interface MutationResponse {
  message?: string;
  success?: boolean;
}

interface ShareLinkResponse {
  share_link?: ShareLink;
  url?: string;
  message?: string;
}

interface DownloadUrlResponse {
  url: string;
  expires_at?: string;
}

interface BulkOperationResponse {
  success: boolean;
  deleted?: number;
  moved?: number;
  message?: string;
}

class FileService {
  // Helper method to get the correct API endpoint
  private getFilesEndpoint(projectId: string): string {
    return `/projects/${projectId}/files`;
  }

  // =====================
  // FOLDER OPERATIONS
  // =====================

  // Get all folders for a project
  async getFolders(projectId: string) {
    try {
      const endpoint = `${this.getFilesEndpoint(projectId)}/folders`;
      const response = await api.get<FoldersListResponse>(endpoint);
      const data = response.data as FoldersListResponse;

      return {
        success: true,
        folders: data.folders || [],
        total: data.total || data.folders?.length || 0,
      };
    } catch (error) {
      console.error('Failed to fetch folders:', error);
      return {
        success: false,
        message: 'Failed to load folders',
        folders: [],
        total: 0,
      };
    }
  }

  // Get folder tree structure
  async getFolderTree(projectId: string) {
    try {
      const endpoint = `${this.getFilesEndpoint(projectId)}/folders/tree`;
      const response = await api.get<FolderTreeResponse>(endpoint);
      const data = response.data as FolderTreeResponse;

      return {
        success: true,
        tree: data.tree || [],
      };
    } catch (error) {
      console.error('Failed to fetch folder tree:', error);
      return {
        success: false,
        message: 'Failed to load folder structure',
        tree: [],
      };
    }
  }

  // Initialize default project folders
  async initializeProjectFolders(projectId: string) {
    try {
      const endpoint = `${this.getFilesEndpoint(projectId)}/folders/initialize`;
      const response = await api.post<FoldersListResponse>(endpoint);
      const data = response.data as FoldersListResponse;

      return {
        success: true,
        folders: data.folders || [],
        message: 'Folders initialized successfully',
      };
    } catch (error) {
      console.error('Failed to initialize folders:', error);
      return {
        success: false,
        message: 'Failed to initialize folders',
        folders: [],
      };
    }
  }

  // Create a new folder
  async createFolder(projectId: string, folderData: CreateFolderData) {
    try {
      const endpoint = `${this.getFilesEndpoint(projectId)}/folders`;
      const response = await api.post<ProjectFolder>(endpoint, folderData);
      const data = response.data as ProjectFolder;

      return {
        success: true,
        folder: data,
        message: 'Folder created successfully',
      };
    } catch (error) {
      console.error('Failed to create folder:', error);
      return {
        success: false,
        message: 'Failed to create folder',
      };
    }
  }

  // Update a folder
  async updateFolder(projectId: string, folderId: string, folderData: UpdateFolderData) {
    try {
      const endpoint = `${this.getFilesEndpoint(projectId)}/folders/${folderId}`;
      const response = await api.put<ProjectFolder>(endpoint, folderData);
      const data = response.data as ProjectFolder;

      return {
        success: true,
        folder: data,
        message: 'Folder updated successfully',
      };
    } catch (error) {
      console.error('Failed to update folder:', error);
      return {
        success: false,
        message: 'Failed to update folder',
      };
    }
  }

  // Delete a folder
  async deleteFolder(projectId: string, folderId: string) {
    try {
      const endpoint = `${this.getFilesEndpoint(projectId)}/folders/${folderId}`;
      const response = await api.delete<MutationResponse>(endpoint);
      const data = response.data as MutationResponse;

      return {
        success: true,
        message: data.message || 'Folder deleted successfully',
      };
    } catch (error) {
      console.error('Failed to delete folder:', error);
      return {
        success: false,
        message: 'Failed to delete folder',
      };
    }
  }

  // =====================
  // FILE OPERATIONS
  // =====================

  // Get files with filtering and pagination
  async getFiles(projectId: string, query: FileQuery = {}) {
    try {
      const params = new URLSearchParams();

      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const endpoint = `${this.getFilesEndpoint(projectId)}?${params.toString()}`;
      const response = await api.get<FilesListResponse>(endpoint);
      const data = response.data as FilesListResponse;

      return {
        success: true,
        files: data.files || [],
        total: data.pagination?.total || data.total || 0,
        page: data.pagination?.page || data.page || 1,
        totalPages: data.pagination?.totalPages || data.totalPages || 1,
      };
    } catch (error) {
      console.error('Failed to fetch files:', error);
      return {
        success: false,
        message: 'Failed to load files',
        files: [],
        total: 0,
        page: 1,
        totalPages: 1,
      };
    }
  }

  // Get single file by ID
  async getFile(projectId: string, fileId: string) {
    try {
      const endpoint = `${this.getFilesEndpoint(projectId)}/${fileId}`;
      const response = await api.get<ProjectFile>(endpoint);
      return {
        success: true,
        file: response.data as ProjectFile,
      };
    } catch (error) {
      console.error('Failed to fetch file:', error);
      return {
        success: false,
        message: 'Failed to load file',
      };
    }
  }

  // Create a file record (after S3 upload)
  async createFile(projectId: string, fileReference: FileReference, fileData: CreateFileData) {
    try {
      const endpoint = this.getFilesEndpoint(projectId);
      const response = await api.post<ProjectFile>(endpoint, {
        file_reference: fileReference,
        ...fileData,
      });
      const data = response.data as ProjectFile;

      return {
        success: true,
        file: data,
        message: 'File created successfully',
      };
    } catch (error) {
      console.error('Failed to create file:', error);
      return {
        success: false,
        message: 'Failed to create file',
      };
    }
  }

  // Create multiple file records (batch upload)
  async createFiles(projectId: string, fileReferences: FileReference[], fileData: CreateFileData) {
    try {
      const endpoint = `${this.getFilesEndpoint(projectId)}/batch`;
      const response = await api.post<{ files: ProjectFile[] }>(endpoint, {
        file_references: fileReferences,
        ...fileData,
      });
      const data = response.data as { files: ProjectFile[] };

      return {
        success: true,
        files: data.files || [],
        message: 'Files created successfully',
      };
    } catch (error) {
      console.error('Failed to create files:', error);
      return {
        success: false,
        message: 'Failed to create files',
        files: [],
      };
    }
  }

  // Update file metadata
  async updateFile(projectId: string, fileId: string, fileData: UpdateFileData) {
    try {
      const endpoint = `${this.getFilesEndpoint(projectId)}/${fileId}`;
      const response = await api.put<ProjectFile>(endpoint, fileData);
      const data = response.data as ProjectFile;

      return {
        success: true,
        file: data,
        message: 'File updated successfully',
      };
    } catch (error) {
      console.error('Failed to update file:', error);
      return {
        success: false,
        message: 'Failed to update file',
      };
    }
  }

  // Delete a file
  async deleteFile(projectId: string, fileId: string) {
    try {
      const endpoint = `${this.getFilesEndpoint(projectId)}/${fileId}`;
      const response = await api.delete<MutationResponse>(endpoint);
      const data = response.data as MutationResponse;

      return {
        success: true,
        message: data.message || 'File deleted successfully',
      };
    } catch (error) {
      console.error('Failed to delete file:', error);
      return {
        success: false,
        message: 'Failed to delete file',
      };
    }
  }

  // Bulk delete files
  async bulkDeleteFiles(projectId: string, fileIds: string[]) {
    try {
      const endpoint = `${this.getFilesEndpoint(projectId)}/bulk-delete`;
      const response = await api.post<BulkOperationResponse>(endpoint, { file_ids: fileIds });
      const data = response.data as BulkOperationResponse;

      return {
        success: true,
        deleted: data.deleted || fileIds.length,
        message: data.message || 'Files deleted successfully',
      };
    } catch (error) {
      console.error('Failed to bulk delete files:', error);
      return {
        success: false,
        message: 'Failed to delete files',
      };
    }
  }

  // Move files to a different folder
  async moveFiles(projectId: string, fileIds: string[], targetFolderId: string) {
    try {
      const endpoint = `${this.getFilesEndpoint(projectId)}/move`;
      const response = await api.post<BulkOperationResponse>(endpoint, {
        file_ids: fileIds,
        target_folder_id: targetFolderId,
      });
      const data = response.data as BulkOperationResponse;

      return {
        success: true,
        moved: data.moved || fileIds.length,
        message: data.message || 'Files moved successfully',
      };
    } catch (error) {
      console.error('Failed to move files:', error);
      return {
        success: false,
        message: 'Failed to move files',
      };
    }
  }

  // Add a new version to a file
  async addFileVersion(
    projectId: string,
    fileId: string,
    fileReference: FileReference,
    comment?: string
  ) {
    try {
      const endpoint = `${this.getFilesEndpoint(projectId)}/${fileId}/versions`;
      const response = await api.post<ProjectFile>(endpoint, {
        file_reference: fileReference,
        comment,
      });
      const data = response.data as ProjectFile;

      return {
        success: true,
        file: data,
        message: 'New version added successfully',
      };
    } catch (error) {
      console.error('Failed to add file version:', error);
      return {
        success: false,
        message: 'Failed to add new version',
      };
    }
  }

  // Get download URL for a file
  async getDownloadUrl(projectId: string, fileId: string) {
    try {
      const endpoint = `${this.getFilesEndpoint(projectId)}/${fileId}/download`;
      const response = await api.get<DownloadUrlResponse>(endpoint);
      const data = response.data as DownloadUrlResponse;

      return {
        success: true,
        url: data.url,
        expires_at: data.expires_at,
      };
    } catch (error) {
      console.error('Failed to get download URL:', error);
      return {
        success: false,
        message: 'Failed to get download URL',
      };
    }
  }

  // Create a share link for a file
  async createShareLink(projectId: string, fileId: string, options: CreateShareLinkData = {}) {
    try {
      const endpoint = `${this.getFilesEndpoint(projectId)}/${fileId}/share`;
      const response = await api.post<ShareLinkResponse>(endpoint, options);
      const data = response.data as ShareLinkResponse;

      return {
        success: true,
        share_link: data.share_link,
        url: data.url,
        message: 'Share link created successfully',
      };
    } catch (error) {
      console.error('Failed to create share link:', error);
      return {
        success: false,
        message: 'Failed to create share link',
      };
    }
  }

  // Access a shared file via token (public endpoint)
  async getSharedFile(token: string, password?: string) {
    try {
      const params = password ? `?password=${encodeURIComponent(password)}` : '';
      const response = await api.get<ProjectFile>(`/files/shared/${token}${params}`);
      return {
        success: true,
        file: response.data as ProjectFile,
      };
    } catch (error) {
      console.error('Failed to access shared file:', error);
      return {
        success: false,
        message: 'Failed to access shared file',
      };
    }
  }

  // Download file directly
  async downloadFile(projectId: string, fileId: string, fileName: string) {
    try {
      const result = await this.getDownloadUrl(projectId, fileId);
      if (result.success && result.url) {
        this.triggerDownload(result.url, fileName);
        return { success: true };
      }
      return result;
    } catch (error) {
      console.error('Failed to download file:', error);
      return {
        success: false,
        message: 'Failed to download file',
      };
    }
  }

  // Helper method to trigger file download
  private triggerDownload(url: string, fileName: string) {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    link.setAttribute('target', '_blank');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // =====================
  // UTILITY METHODS
  // =====================

  // Get file categories for dropdown
  getFileCategories() {
    return [
      { value: 'contract', label: 'Contract', color: 'bg-blue-100 text-blue-800' },
      { value: 'permit', label: 'Permit', color: 'bg-green-100 text-green-800' },
      { value: 'insurance', label: 'Insurance', color: 'bg-purple-100 text-purple-800' },
      { value: 'correspondence', label: 'Correspondence', color: 'bg-yellow-100 text-yellow-800' },
      { value: 'report', label: 'Report', color: 'bg-indigo-100 text-indigo-800' },
      { value: 'submittal', label: 'Submittal', color: 'bg-orange-100 text-orange-800' },
      { value: 'invoice', label: 'Invoice', color: 'bg-teal-100 text-teal-800' },
      { value: 'change_order', label: 'Change Order', color: 'bg-red-100 text-red-800' },
      { value: 'meeting_minutes', label: 'Meeting Minutes', color: 'bg-cyan-100 text-cyan-800' },
      { value: 'safety', label: 'Safety', color: 'bg-rose-100 text-rose-800' },
      { value: 'quality', label: 'Quality', color: 'bg-emerald-100 text-emerald-800' },
      { value: 'other', label: 'Other', color: 'bg-gray-100 text-gray-800' },
    ];
  }

  // Get sort options for dropdown
  getSortOptions() {
    return [
      { value: 'name', label: 'Name' },
      { value: 'created_at', label: 'Date Added' },
      { value: 'updated_at', label: 'Date Modified' },
      { value: 'size', label: 'File Size' },
      { value: 'category', label: 'Category' },
    ];
  }

  // Get category color
  getCategoryColor(category: string): string {
    const categories = this.getFileCategories();
    const found = categories.find(c => c.value === category);
    return found?.color || 'bg-gray-100 text-gray-800';
  }

  // Format file size
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Get file icon based on extension
  getFileIcon(extension: string): string {
    const iconMap: Record<string, string> = {
      pdf: 'file-text',
      doc: 'file-text',
      docx: 'file-text',
      xls: 'file-spreadsheet',
      xlsx: 'file-spreadsheet',
      ppt: 'file-presentation',
      pptx: 'file-presentation',
      jpg: 'image',
      jpeg: 'image',
      png: 'image',
      gif: 'image',
      svg: 'image',
      zip: 'file-archive',
      rar: 'file-archive',
      dwg: 'pen-tool',
      dxf: 'pen-tool',
      default: 'file',
    };
    return iconMap[extension.toLowerCase()] || iconMap.default;
  }
}

export default new FileService();
